/**
 * MovementSystem — mouse-following cell + drifting food + chasing predators.
 *
 * The player cell tracks the cursor with exponential smoothing (~80ms lag),
 * and its Renderable gets squash/stretch along the velocity direction.
 * A trail of past positions is drawn in ParticleSystem.
 *
 * This system also owns the per-frame render tick: it advances Renderable.time
 * and invokes Renderable.draw callbacks so animated entities (flagella,
 * cilia, DNA helix, etc.) stay in motion.
 */

import type { World } from '@/core/ECS';
import { Position, Velocity, PlayerCell, Drift, Hostile, Renderable, Absorbable } from '@/components';
import { smoothApproach, clamp, TAU } from '@/utils/math';
import { drawCellBody, drawCellHalo } from '@/visuals/bio';
import { player } from './PlayerState';
import { bus } from '@/core/EventBus';
import type { MutationSystem } from './MutationSystem';

// AI tuning constants for predator pack behavior.
// SEPARATION_RADIUS: predators within this distance push away from each
//   other, so packs spread instead of stacking on the same point.
// SINGLE_STRIKER_RADIUS: at any moment, only one predator within this range
//   of the player may be in the 'strike' phase — others delay their lunge.
//   Without this rule, multiple predators stack their strikes into a single
//   unavoidable burst.
const SEPARATION_RADIUS = 80;
const SEPARATION_FORCE = 220;
const SINGLE_STRIKER_RADIUS = 250;
// Predators only chase when the player is closer than this. Beyond it,
// they wander idly. Lets us keep a screen full of predators without
// having a swarm constantly converging on the cell — the player only
// engages the ones they choose to approach. Spore swarms ignore this
// (they're a roaming threat by design).
const AGGRO_RANGE = 280;
const ALWAYS_AGGRO: ReadonlySet<string> = new Set(['spore_swarm']);

/** Random multiplier in [0.85, 1.15] used to desync predator phase timers. */
function jitter(): number {
  return 0.85 + Math.random() * 0.3;
}

/** Roll fresh per-strike speed + arc so consecutive strikes don't read the
 *  same. Called on every transition into the 'strike' phase. */
function rollStrike(h: { strikeMult?: number; strikeArc?: number; flankBias?: number }): void {
  h.strikeMult = 0.8 + Math.random() * 0.5;     // 0.8..1.3
  // Bias the arc toward the predator's current flank direction so the
  // tangential offset reads as "swooping in from this side", not random.
  h.strikeArc = (Math.random() * 0.6 - 0.3) + 0.25 * (h.flankBias ?? 0);
}

export class MovementSystem {
  private prevTargetX = 0;
  private prevTargetY = 0;

  constructor(private mutations?: MutationSystem) {}

  update(world: World, dt: number, screenW: number, screenH: number): void {
    // --- player cell ---
    for (const e of world.query([Position, Velocity, PlayerCell, Renderable])) {
      const p = world.get(e, Position)!;
      const v = world.get(e, Velocity)!;
      const cell = world.get(e, PlayerCell)!;
      const r = world.get(e, Renderable)!;

      // update stats reference (radius may have changed from upgrades)
      cell.radius = player.stats.cellRadius;
      cell.absorptionRadius = player.stats.absorptionRadius;

      // halo: only redraw when the field radius or pulse state changes.
      // When the player isn't holding right-click the halo collapses to
      // the cell radius (no visible field) — that's what gates the
      // same-type streak combo.
      const fieldNow = !cell.haloEnabled
        ? cell.radius
        : (cell.pulseRemaining > 0
          ? cell.absorptionRadius * (1 + clamp(cell.pulseRemaining / 1.0, 0, 1))
          : cell.absorptionRadius);
      if ((r as any)._lastField !== fieldNow || (r as any)._lastRadius !== cell.radius) {
        drawCellHalo(r.glow!, fieldNow);
        (r as any)._lastField = fieldNow;
        (r as any)._lastRadius = cell.radius;
      }

      r.baseScale = 1;

      // dash takes over motion briefly
      if (cell.dashRemaining > 0) {
        p.x += cell.dashVx * dt;
        p.y += cell.dashVy * dt;
        v.vx = cell.dashVx;
        v.vy = cell.dashVy;
        cell.dashRemaining -= dt;
      } else {
        // smooth follow of the cursor target. Base rate is intentionally
        // sluggish — the cell is a fragile glob floating in cytoplasm.
        // Level the Cytoplasmic Flow upgrade to tighten the follow.
        const rate = 1.4 * player.stats.moveSpeedMult;
        const nx = smoothApproach(p.x, cell.targetX, rate, dt);
        const ny = smoothApproach(p.y, cell.targetY, rate, dt);
        v.vx = (nx - p.x) / Math.max(dt, 1e-4);
        v.vy = (ny - p.y) / Math.max(dt, 1e-4);
        p.x = nx; p.y = ny;
      }

      if (cell.invulnRemaining > 0) cell.invulnRemaining -= dt;
      if (cell.pulseRemaining > 0) cell.pulseRemaining -= dt;

      p.x = clamp(p.x, 0, screenW);
      p.y = clamp(p.y, 0, screenH);

      // animate the membrane and organelles every frame
      r.time += dt;
      const owned = this.mutations ? this.mutations.ownedIds() : [];
      drawCellBody(r.body, { radius: cell.radius, fieldRadius: fieldNow }, r.time, owned);

      // shield ring — visible whenever invuln is active (shield pickup gives
      // 5s, hit-invuln gives a tiny grace period; both read the same field)
      if (cell.invulnRemaining > 0.1) {
        const fadeIn = Math.min(1, (5 - cell.invulnRemaining) * 4);
        const fadeOut = Math.min(1, cell.invulnRemaining);
        const alpha = 0.9 * fadeIn * fadeOut;
        const pulse = 1 + Math.sin(r.time * 6) * 0.06;
        const outerR = (cell.radius + 14) * pulse;
        const innerR = (cell.radius + 7) * pulse;
        r.body.circle(0, 0, outerR).stroke({ color: 0x4fd1c5, width: 2.5, alpha });
        r.body.circle(0, 0, innerR).stroke({ color: 0x4fd1c5, width: 1.2, alpha: alpha * 0.55 });
      }

      // trail sample (used by ParticleSystem to render the tail)
      cell.trail.push({ x: p.x, y: p.y, age: 0 });
      if (cell.trail.length > 18) cell.trail.shift();
      for (const t of cell.trail) t.age += dt;

      // breathing + squash/stretch
      cell.phase += dt;
      const speed = Math.hypot(v.vx, v.vy);
      const stretch = clamp(speed / 500, 0, 0.35);
      const breathe = Math.sin(cell.phase * 1.8) * 0.04 * (1 - clamp(speed / 120, 0, 1));
      const angle = Math.atan2(v.vy, v.vx);
      r.root.rotation = Number.isFinite(angle) ? angle : 0;
      const sx = 1 + stretch + breathe;
      const sy = 1 - stretch * 0.8 + breathe;
      r.root.scale.set(sx, sy);
      r.root.position.set(p.x, p.y);

      bus.emit('player:moved', { x: p.x, y: p.y });
    }

    // --- drifting food (Brownian wander) ---
    for (const e of world.query([Position, Velocity, Drift])) {
      if (world.has(e, PlayerCell)) continue;
      const p = world.get(e, Position)!;
      const v = world.get(e, Velocity)!;
      const d = world.get(e, Drift)!;
      // slowly drift base velocity direction
      d.wanderSeed += dt * 0.7;
      const wanderX = Math.sin(d.wanderSeed) * d.strength;
      const wanderY = Math.cos(d.wanderSeed * 0.83 + 1.2) * d.strength;
      v.vx = d.baseVx + wanderX;
      v.vy = d.baseVy + wanderY;
      p.x += v.vx * dt;
      p.y += v.vy * dt;
      // wrap around the petri dish edges
      if (p.x < -20) p.x = screenW + 10;
      if (p.x > screenW + 20) p.x = -10;
      if (p.y < -20) p.y = screenH + 10;
      if (p.y > screenH + 20) p.y = -10;

      const r = world.get(e, Renderable);
      if (r) {
        r.root.position.set(p.x, p.y);
        r.time += dt;
        // swimming food (bacteria) aligns to velocity; drifters stay upright
        const speed2 = Math.hypot(v.vx, v.vy);
        if (speed2 > 30) {
          r.root.rotation = Math.atan2(v.vy, v.vx);
        }
        r.draw?.(r.time, r.body, r.glow);
        if (world.has(e, Absorbable)) {
          // subtle size pulse — makes the scene feel alive
          const pulse = 1 + Math.sin((d.wanderSeed + d.strength) * 2.2) * 0.06;
          r.root.scale.set(pulse);
        }
      }
    }

    // --- predators ---
    let playerPos: { x: number; y: number } | null = null;
    let playerVel = { vx: 0, vy: 0 };
    for (const e of world.query([PlayerCell, Position, Velocity])) {
      const p = world.get(e, Position)!;
      const pv = world.get(e, Velocity)!;
      playerPos = { x: p.x, y: p.y };
      playerVel = { vx: pv.vx, vy: pv.vy };
    }

    // Snapshot all predator positions so per-predator AI can do separation
    // and single-striker checks against the *current* field, without each
    // predator re-querying. Also count strikers near the player — used to
    // enforce the single-striker rule below.
    const predatorSnapshot: { x: number; y: number; striking: boolean; id: number }[] = [];
    let activeStrikersNearPlayer = 0;
    for (const e of world.query([Position, Hostile])) {
      const p = world.get(e, Position)!;
      const h = world.get(e, Hostile)!;
      const striking = h.aiPhase === 'strike';
      predatorSnapshot.push({ x: p.x, y: p.y, striking, id: e });
      if (striking && playerPos) {
        const d = Math.hypot(playerPos.x - p.x, playerPos.y - p.y);
        if (d < SINGLE_STRIKER_RADIUS) activeStrikersNearPlayer++;
      }
    }

    for (const e of world.query([Position, Velocity, Hostile, Renderable])) {
      const p = world.get(e, Position)!;
      const v = world.get(e, Velocity)!;
      const h = world.get(e, Hostile)!;
      const r = world.get(e, Renderable)!;

      if (h.attackTimer > 0) h.attackTimer -= dt;

      // lazy-init AI state on first tick so we don't touch createPredator
      if (h.aiPhase === undefined) {
        h.aiPhase = 'idle';
        h.phaseTimer = 0;
        h.flankBias = Math.random() < 0.5 ? -1 : 1;
        h.flankSwapTimer = 2 + Math.random() * 1.5;
        h.surpriseCooldown = 4 + Math.random() * 3;
        h.visualScale = 1;
        // Give each predator a random orbit angle so the pack distributes
        // around the player instead of converging on the cursor. Slowly
        // drifts each tick to keep the spread organic.
        h.orbitAngle = Math.random() * Math.PI * 2;
        // Per-predator orbit radius and lead-aim factor — varies the
        // approach geometry so a pack doesn't all line up on the same
        // ring or all lead the player by the same amount. Faster
        // predators (paramecium, spore swarm) get a longer lead since
        // their strikes cross more ground in the same time.
        h.orbitR = 70 + Math.random() * 70;
        h.leadFactor = (h.speed > 90 ? 0.3 : 0.2) + Math.random() * 0.15;
        h.strikeMult = 1;
        h.strikeArc = 0;
      }

      // drift the orbit angle so the pack circulates
      h.orbitAngle = (h.orbitAngle ?? 0) + dt * 0.35 * (h.flankBias ?? 1);

      let targetVx = 0, targetVy = 0;
      let desiredScale = 1;

      // Aggro gate: only chase / strike when the player is close OR this
      // predator is in the always-aggro set. When far, drift in a random
      // direction that re-rolls every few seconds (cheap "wander" mode).
      // Reset chase phase to idle so a predator that's been winding up
      // doesn't lunge as soon as the player wanders within range.
      const playerDistSq = playerPos
        ? (playerPos.x - p.x) ** 2 + (playerPos.y - p.y) ** 2
        : Infinity;
      const inAggro = playerPos && (
        ALWAYS_AGGRO.has(h.predatorId) || playerDistSq < AGGRO_RANGE * AGGRO_RANGE
      );

      if (playerPos) {
       if (inAggro) {
        // Lead-aim: predict where the player will be when the strike
        // lands by adding velocity * leadFactor. This makes predators
        // visibly read the cursor's drift instead of always pointing
        // at the cell's current position. Each predator has its own
        // leadFactor so a pack arrives at slightly different intercepts.
        const lf = h.leadFactor ?? 0.25;
        const aimX = playerPos.x + playerVel.vx * lf;
        const aimY = playerPos.y + playerVel.vy * lf;
        const pdx = aimX - p.x;
        const pdy = aimY - p.y;
        const pd = Math.hypot(pdx, pdy) || 1;
        const pnx = pdx / pd, pny = pdy / pd;

        const orbitR = h.orbitR ?? 90;
        const ox = playerPos.x + Math.cos(h.orbitAngle ?? 0) * orbitR;
        const oy = playerPos.y + Math.sin(h.orbitAngle ?? 0) * orbitR;
        const dx = ox - p.x;
        const dy = oy - p.y;
        const d = Math.hypot(dx, dy) || 1;
        // default/chase uses orbit vector so predators scatter around the
        // player; strikes below re-read pnx/pny to aim true.
        const nx = dx / d, ny = dy / d;
        // perpendicular (left-hand) unit vector for flanking
        const px = -ny, py = nx;

        if (h.behavior === 'ambush') {
          // Dinoflagellate loop: idle (drift) → windup (stop, grow) →
          // strike (fast burst) → recover (shrink + approach) → idle.
          h.phaseTimer = (h.phaseTimer ?? 0) - dt;
          if (h.phaseTimer <= 0) {
            switch (h.aiPhase) {
              case 'idle':    h.aiPhase = 'windup';  h.phaseTimer = 0.6 * jitter(); break;
              case 'windup':
                // single-striker rule: if another predator is already
                // striking near the player, hold the windup an extra beat
                // instead of stacking the lunge.
                if (activeStrikersNearPlayer > 0) {
                  h.phaseTimer = 0.3 + Math.random() * 0.3;
                } else {
                  h.aiPhase = 'strike'; h.phaseTimer = 0.5 * jitter();
                  activeStrikersNearPlayer++;
                  rollStrike(h);
                }
                break;
              case 'strike':  h.aiPhase = 'recover'; h.phaseTimer = 1.0 * jitter(); break;
              case 'recover':
              default:        h.aiPhase = 'idle';    h.phaseTimer = (1.5 + Math.random() * 1.2) * jitter(); break;
            }
          }
          switch (h.aiPhase) {
            case 'idle':
              // near-stationary drift perpendicular to the player
              targetVx = px * h.speed * 0.18;
              targetVy = py * h.speed * 0.18;
              desiredScale = 0.9;
              break;
            case 'windup':
              // stop and telegraph: grow + orient toward player
              targetVx = nx * h.speed * 0.05;
              targetVy = ny * h.speed * 0.05;
              desiredScale = 1.35;
              break;
            case 'strike': {
              // explosive charge — lead-aim with a tangential arc and
              // a per-strike speed jitter so consecutive ambush lunges
              // can't be pre-rolled by the player.
              const sm = h.strikeMult ?? 1;
              const arc = h.strikeArc ?? 0;
              const tx = -pny, ty = pnx;
              targetVx = (pnx + tx * arc) * h.speed * 2.6 * sm;
              targetVy = (pny + ty * arc) * h.speed * 2.6 * sm;
              desiredScale = 1.15;
              break;
            }
            case 'recover':
              // overshoot recovery: slower than normal chase
              targetVx = nx * h.speed * 0.6;
              targetVy = ny * h.speed * 0.6;
              desiredScale = 0.85;
              break;
          }
        } else if (h.predatorId === 'paramecium_hunter') {
          // Dart pattern: bursts of high speed with brief pauses to reorient.
          // Surprise charges skip the pause and sprint straight through.
          h.phaseTimer = (h.phaseTimer ?? 0) - dt;
          h.surpriseCooldown = (h.surpriseCooldown ?? 0) - dt;
          if (h.phaseTimer <= 0) {
            if (h.aiPhase === 'strike' && h.surpriseCooldown! <= 0 && Math.random() < 0.3) {
              // chain into a surprise sprint
              h.aiPhase = 'windup';
              h.phaseTimer = 0.35 * jitter();
              h.surpriseCooldown = 6 + Math.random() * 4;
            } else if (h.aiPhase === 'idle') {
              if (activeStrikersNearPlayer > 0) {
                // hold idle a beat — let the current striker resolve
                h.phaseTimer = 0.4 + Math.random() * 0.3;
              } else {
                h.aiPhase = 'strike'; h.phaseTimer = 0.85 * jitter();
                activeStrikersNearPlayer++;
                rollStrike(h);
              }
            } else {
              h.aiPhase = 'idle'; h.phaseTimer = 0.35 * jitter();
            }
          }
          if (h.aiPhase === 'windup') {
            targetVx = nx * h.speed * 0.1;
            targetVy = ny * h.speed * 0.1;
            desiredScale = 1.25;
            if (h.phaseTimer <= 0) {
              if (activeStrikersNearPlayer > 0) {
                h.phaseTimer = 0.2 + Math.random() * 0.2;
              } else {
                h.aiPhase = 'strike'; h.phaseTimer = 0.7 * jitter();
                activeStrikersNearPlayer++;
                rollStrike(h);
              }
            }
          } else if (h.aiPhase === 'strike') {
            // lead-aimed dart with a tangential arc — combined with the
            // per-strike speed jitter, the dodge window varies each time.
            const arc = h.strikeArc ?? 0;
            const sm = h.strikeMult ?? 1;
            const spx = -pny, spy = pnx;
            targetVx = (pnx + spx * arc) * h.speed * 1.35 * sm;
            targetVy = (pny + spy * arc) * h.speed * 1.35 * sm;
            desiredScale = 1.05;
          } else {
            // idle pause — decelerate and slightly shrink
            targetVx = nx * h.speed * 0.12;
            targetVy = ny * h.speed * 0.12;
            desiredScale = 0.92;
          }
        } else {
          // Default chase (amoeba): flanking approach with occasional
          // telegraphed surprise lunges. The flank bias flips every few
          // seconds so it reads as a predator weaving in, not homing.
          h.flankSwapTimer = (h.flankSwapTimer ?? 0) - dt;
          if (h.flankSwapTimer <= 0) {
            h.flankBias = -h.flankBias!;
            h.flankSwapTimer = 2 + Math.random() * 1.8;
          }
          h.surpriseCooldown = (h.surpriseCooldown ?? 0) - dt;
          h.phaseTimer = (h.phaseTimer ?? 0) - dt;
          const playerDist = Math.hypot(playerPos.x - p.x, playerPos.y - p.y);
          if (h.aiPhase !== 'windup' && h.aiPhase !== 'strike'
              && h.surpriseCooldown! <= 0 && playerDist < 380 && Math.random() < 0.6 * dt
              && activeStrikersNearPlayer === 0) {
            h.aiPhase = 'windup';
            h.phaseTimer = 0.45 * jitter();
            h.surpriseCooldown = 5 + Math.random() * 4;
          }
          if (h.aiPhase === 'windup') {
            // telegraph: stop and puff up
            targetVx = 0; targetVy = 0;
            desiredScale = 1.3;
            if (h.phaseTimer! <= 0) {
              if (activeStrikersNearPlayer > 0) {
                h.phaseTimer = 0.2 + Math.random() * 0.2;
              } else {
                h.aiPhase = 'strike'; h.phaseTimer = 0.55 * jitter();
                activeStrikersNearPlayer++;
                rollStrike(h);
              }
            }
          } else if (h.aiPhase === 'strike') {
            // Lead-aimed strike with a per-strike arc + speed jitter so
            // surprise lunges don't all read as the same swoop.
            const arc = h.strikeArc ?? 0;
            const sm = h.strikeMult ?? 1;
            const spx = -pny, spy = pnx;
            targetVx = (pnx + spx * arc) * h.speed * 2.1 * sm;
            targetVy = (pny + spy * arc) * h.speed * 2.1 * sm;
            desiredScale = 1.1;
            if (h.phaseTimer! <= 0) { h.aiPhase = 'recover'; h.phaseTimer = 0.4 * jitter(); }
          } else if (h.aiPhase === 'recover') {
            targetVx = -nx * h.speed * 0.3;
            targetVy = -ny * h.speed * 0.3;
            desiredScale = 0.88;
            if (h.phaseTimer! <= 0) { h.aiPhase = 'idle'; h.phaseTimer = 0; }
          } else {
            // normal flanking chase — mix forward + tangent
            const flank = 0.55 * h.flankBias!;
            targetVx = (nx + px * flank) * h.speed;
            targetVy = (ny + py * flank) * h.speed;
            desiredScale = 1 + Math.sin(h.flankSwapTimer! * 2) * 0.05;
          }
        }
       } else {
        // === wander mode (player out of aggro range) ===
        // Random-direction drift that re-rolls every few seconds, with
        // a soft edge bounce so wanderers don't pin to the screen border.
        // Reset chase phase so a predator winding up at long range
        // doesn't auto-strike the moment the player drifts past AGGRO_RANGE.
        h.aiPhase = 'idle';
        h.phaseTimer = 0;
        h.wanderTimer = (h.wanderTimer ?? 0) - dt;
        if (h.wanderTimer <= 0 || h.wanderAngle == null) {
          h.wanderAngle = Math.random() * Math.PI * 2;
          h.wanderTimer = 2 + Math.random() * 2.5;
        }
        const margin = 60;
        let wa = h.wanderAngle;
        if (p.x < margin && Math.cos(wa) < 0) wa = Math.PI - wa;
        if (p.x > screenW - margin && Math.cos(wa) > 0) wa = Math.PI - wa;
        if (p.y < margin && Math.sin(wa) < 0) wa = -wa;
        if (p.y > screenH - margin && Math.sin(wa) > 0) wa = -wa;
        h.wanderAngle = wa;
        targetVx = Math.cos(wa) * h.speed * 0.28;
        targetVy = Math.sin(wa) * h.speed * 0.28;
        desiredScale = 0.95;
       }

        // separation: push away from nearby predators so the pack spreads
        // out rather than stacking on one orbit point. Skip during 'strike'
        // so a committed lunge isn't deflected by a neighbor.
        if (h.aiPhase !== 'strike') {
          let sepX = 0, sepY = 0;
          for (const other of predatorSnapshot) {
            if (other.id === e) continue;
            const ddx = p.x - other.x;
            const ddy = p.y - other.y;
            const ddist = Math.hypot(ddx, ddy);
            if (ddist > 0 && ddist < SEPARATION_RADIUS) {
              const w = (SEPARATION_RADIUS - ddist) / SEPARATION_RADIUS;
              sepX += (ddx / ddist) * w;
              sepY += (ddy / ddist) * w;
            }
          }
          targetVx += sepX * SEPARATION_FORCE;
          targetVy += sepY * SEPARATION_FORCE;
        }

        // steer toward target velocity (smooth). Strike phases respond
        // faster so the burst feels punchy.
        const steerRate = h.aiPhase === 'strike' ? 10 : 4;
        v.vx = smoothApproach(v.vx, targetVx, steerRate, dt);
        v.vy = smoothApproach(v.vy, targetVy, steerRate, dt);
      }

      // smooth the visual scale toward the desired phase value
      h.visualScale = smoothApproach(h.visualScale ?? 1, desiredScale, 6, dt);

      p.x += v.vx * dt;
      p.y += v.vy * dt;
      p.x = clamp(p.x, -50, screenW + 50);
      p.y = clamp(p.y, -50, screenH + 50);
      r.root.position.set(p.x, p.y);
      // rotate toward velocity
      if (Math.hypot(v.vx, v.vy) > 1) {
        r.root.rotation = Math.atan2(v.vy, v.vx);
      }
      // animate the body (cilia, pseudopods, flagella, etc.)
      r.time += dt;
      r.draw?.(r.time, r.body, r.glow);
      // phase-driven scale + gentle breathing overlay
      const breathe = 1 + Math.sin(performance.now() * 0.003 + e) * 0.03;
      r.root.scale.set(h.visualScale * breathe);
    }

    // scrub unused variable
    void TAU;
  }

  setPlayerTarget(world: World, x: number, y: number): void {
    for (const e of world.query([PlayerCell])) {
      const cell = world.get(e, PlayerCell)!;
      cell.targetX = x;
      cell.targetY = y;
    }
    this.prevTargetX = x;
    this.prevTargetY = y;
  }
}
